import React, { useEffect, useMemo } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { getTheme } from "@/components/theme";
import { useAuth } from "@/contexts/AuthContext";
import { resolveThemeFontId, themeFontFamilyCss } from "@/theme/fontCatalog";
import { TranslatorProvider } from "@/components/TranslatorProvider";
import WithAuthentication from "./core/WithAuthentication";
import WithSnackbar from "./core/WithSnackbar";
import { BrowserRouter as Router } from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeModeProvider, useThemeMode } from "@/contexts/ThemeModeContext";
import { AccentColorsProvider, useAccentColors } from "@/contexts/AccentColorsContext";
import { NotificationProvider } from "@/contexts/NotificationContext";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
    },
  },
});

function ThemedApp() {
  const { mode } = useThemeMode();
  const { lightAccent, darkAccent } = useAccentColors();
  const { user } = useAuth();
  const fontStack = useMemo(() => {
    const key = user?.currentBusinessUnit?.themeFontKey;
    return themeFontFamilyCss(resolveThemeFontId(key));
  }, [user?.currentBusinessUnit?.themeFontKey]);

  const primaryMain = mode === 'light' ? lightAccent : darkAccent;

  useEffect(() => {
    document.documentElement.style.setProperty('--fsm-ui-font-family', fontStack);
  }, [fontStack]);

  useEffect(() => {
    document.documentElement.style.setProperty('--fsm-accent', primaryMain);
  }, [primaryMain]);

  const theme = useMemo(
    () => getTheme(mode, fontStack, primaryMain),
    [mode, fontStack, primaryMain],
  );
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <WithSnackbar>
        <NotificationProvider>
          <Router>
            <WithAuthentication />
          </Router>
        </NotificationProvider>
      </WithSnackbar>
    </ThemeProvider>
  );
}

const ClientApp = (props: any) => {
    return (
        <>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <TranslatorProvider>
                        <ThemeModeProvider>
                            <AccentColorsProvider>
                                <ThemedApp />
                            </AccentColorsProvider>
                        </ThemeModeProvider>
                    </TranslatorProvider>
                </AuthProvider>
            </QueryClientProvider>
        </>
    );
};

export default ClientApp;


